import { useParams, useNavigate } from 'react-router-dom';
import { getDemoCompany } from '@/data/demoCompanyBranding';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import {
  Home,
  Play,
  TrendingUp,
  Trophy,
  Settings,
  Users,
  Activity,
  Target,
  Clock,
  Star,
  ChevronRight,
  LogOut,
  BarChart3,
  Zap,
} from 'lucide-react';

export default function DemoDashboard() {
  const { companySlug } = useParams<{ companySlug: string }>();
  const navigate = useNavigate();
  const company = getDemoCompany(companySlug || '');

  if (!company) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <Card className="p-8 text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Demo Not Found</h1>
          <p className="text-gray-600 mb-6">The requested company demo does not exist.</p>
          <Button onClick={() => navigate('/')}>Return Home</Button>
        </Card>
      </div>
    );
  }

  const navItems = [
    { icon: Home, label: 'Dashboard', active: true },
    { icon: Play, label: 'Simulations', active: false },
    { icon: TrendingUp, label: 'My Progress', active: false },
    { icon: Trophy, label: 'Leaderboards', active: false },
    { icon: Settings, label: 'Settings', active: false },
  ];

  return (
    <div 
      className="min-h-screen flex bg-gray-50"
      style={{
        '--demo-primary': company.primaryColor,
        '--demo-primary-hex': company.primaryColorHex,
      } as React.CSSProperties}
    >
      {/* Demo Banner */}
      <div className="fixed top-0 left-0 right-0 bg-gray-900 text-white py-2 px-4 text-center text-sm z-50">
        <span className="opacity-75">Demo Preview:</span>
        <span className="font-semibold mx-1">{company.name}</span>
        <span className="opacity-75">White-Label Dashboard</span>
        <Button 
          variant="link" 
          className="text-white underline ml-2 p-0 h-auto"
          onClick={() => navigate('/')}
        >
          Exit Demo
        </Button>
      </div>

      {/* Sidebar */}
      <aside 
        className="w-64 min-h-screen pt-12 flex flex-col"
        style={{ backgroundColor: `hsl(${company.primaryColor})` }}
      >
        {/* Logo */}
        <div className="p-6 border-b border-white/20">
          <div className="bg-white rounded-lg p-3 inline-block">
            <img 
              src={company.logoUrl}
              alt={company.name}
              className="h-8 w-auto object-contain"
              onError={(e) => {
                (e.target as HTMLImageElement).src = 'https://placehold.co/150x40?text=' + encodeURIComponent(company.name);
              }}
            />
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-1">
          {navItems.map((item) => (
            <button
              key={item.label}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-colors ${
                item.active 
                  ? 'bg-white/20 text-white font-medium' 
                  : 'text-white/70 hover:bg-white/10 hover:text-white'
              }`}
            >
              <item.icon className="h-5 w-5" />
              {item.label}
            </button>
          ))}
        </nav>

        {/* User Section */}
        <div className="p-4 border-t border-white/20">
          <div className="flex items-center gap-3 px-2">
            <Avatar className="h-10 w-10 border-2 border-white/30">
              <AvatarFallback 
                className="text-white text-sm"
                style={{ backgroundColor: `hsl(${company.accentColor})` }}
              >
                JD
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <p className="text-white font-medium text-sm truncate">John Demo</p>
              <p className="text-white/60 text-xs truncate">Sales Representative</p>
            </div>
            <Button variant="ghost" size="icon" className="text-white/70 hover:text-white hover:bg-white/10">
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 pt-12">
        {/* Header */}
        <header className="bg-white border-b px-8 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Welcome back, John!</h1>
              <p className="text-gray-500">Ready to continue your training journey?</p>
            </div>
            <div className="flex items-center gap-4">
              <Badge 
                className="text-white px-3 py-1"
                style={{ backgroundColor: `hsl(${company.primaryColor})` }}
              >
                {company.name}
              </Badge>
            </div>
          </div>
        </header>

        {/* Dashboard Content */}
        <div className="p-8 space-y-8">
          {/* Stats Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Active Team Members</p>
                    <p className="text-3xl font-bold">{company.sampleStats.teamMembers}</p>
                  </div>
                  <div 
                    className="h-12 w-12 rounded-full flex items-center justify-center"
                    style={{ backgroundColor: `hsl(${company.primaryColor} / 0.1)` }}
                  >
                    <Users 
                      className="h-6 w-6" 
                      style={{ color: `hsl(${company.primaryColor})` }}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Simulations Completed</p>
                    <p className="text-3xl font-bold">{company.sampleStats.simulationsCompleted.toLocaleString()}</p>
                  </div>
                  <div 
                    className="h-12 w-12 rounded-full flex items-center justify-center"
                    style={{ backgroundColor: `hsl(${company.primaryColor} / 0.1)` }}
                  >
                    <Activity 
                      className="h-6 w-6"
                      style={{ color: `hsl(${company.primaryColor})` }}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Average Score</p>
                    <p className="text-3xl font-bold">{company.sampleStats.avgScore}%</p>
                  </div>
                  <div 
                    className="h-12 w-12 rounded-full flex items-center justify-center"
                    style={{ backgroundColor: `hsl(${company.primaryColor} / 0.1)` }}
                  >
                    <Target 
                      className="h-6 w-6"
                      style={{ color: `hsl(${company.primaryColor})` }}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Your Rank</p>
                    <p className="text-3xl font-bold">#12</p>
                  </div>
                  <div 
                    className="h-12 w-12 rounded-full flex items-center justify-center"
                    style={{ backgroundColor: `hsl(${company.primaryColor} / 0.1)` }}
                  >
                    <Trophy 
                      className="h-6 w-6"
                      style={{ color: `hsl(${company.primaryColor})` }}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Progress Section */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Zap className="h-5 w-5" style={{ color: `hsl(${company.primaryColor})` }} />
                Your Training Progress
              </CardTitle>
              <CardDescription>Keep up the great work! You're on track.</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-muted-foreground">Monthly Goal Progress</span>
                    <span className="font-medium">8/10 simulations</span>
                  </div>
                  <Progress value={80} className="h-3" />
                </div>
                <div className="grid grid-cols-3 gap-4 pt-4">
                  <div className="text-center p-4 bg-gray-50 rounded-lg">
                    <BarChart3 className="h-6 w-6 mx-auto mb-2 text-gray-400" />
                    <p className="text-2xl font-bold">23</p>
                    <p className="text-xs text-muted-foreground">Total Sessions</p>
                  </div>
                  <div className="text-center p-4 bg-gray-50 rounded-lg">
                    <Clock className="h-6 w-6 mx-auto mb-2 text-gray-400" />
                    <p className="text-2xl font-bold">4.2h</p>
                    <p className="text-xs text-muted-foreground">Training Time</p>
                  </div>
                  <div className="text-center p-4 bg-gray-50 rounded-lg">
                    <Star className="h-6 w-6 mx-auto mb-2 text-gray-400" />
                    <p className="text-2xl font-bold">5</p>
                    <p className="text-xs text-muted-foreground">Badges Earned</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Available Simulations */}
          <Card>
            <CardHeader>
              <CardTitle>Available Simulations</CardTitle>
              <CardDescription>Start a new training session</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {company.sampleSimulations.map((sim, index) => (
                  <div 
                    key={index}
                    className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                  >
                    <div className="flex items-center gap-4">
                      <div 
                        className="h-12 w-12 rounded-lg flex items-center justify-center"
                        style={{ backgroundColor: `hsl(${company.primaryColor} / 0.1)` }}
                      >
                        <Play 
                          className="h-5 w-5"
                          style={{ color: `hsl(${company.primaryColor})` }}
                        />
                      </div>
                      <div>
                        <p className="font-medium">{sim.name}</p>
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <span>{sim.type}</span>
                          <span>•</span>
                          <span>{sim.difficulty}</span>
                          <span>•</span>
                          <span>{sim.duration}</span>
                        </div>
                      </div>
                    </div>
                    <Button 
                      size="sm"
                      style={{ 
                        backgroundColor: `hsl(${company.primaryColor})`,
                        color: 'white'
                      }}
                    >
                      Start
                      <ChevronRight className="ml-1 h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
}
